import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, EMPTY, first, Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { UserService } from 'src/app/core/services/user.service';
import { indicate } from 'src/app/operators';
import {
  CommentResolutionStatus,
  ReleaseCommentViewModel,
  ReleaseRoleViewModel,
  ReleaseStatus,
  UserType,
} from 'src/app/shared/models/autogenerated-piping';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { getApiErrorMessage } from 'src/app/shared/utils';
import { ReleaseCommentsService } from '../../../services/release-comments.service';

/**
 * Loaded once per release and reused to build the "Comments (n) - status" tab label
 * without re-fetching the whole thread just for the count.
 */
export interface ReleaseCommentsTabSummary {
  totalCount: number;
  hasOpenComments: boolean;
}

@Component({
  selector: 'app-release-comments-tab',
  templateUrl: './release-comments-tab.component.html',
  styleUrls: ['./release-comments-tab.component.scss'],
  standalone: false,
})
export class ReleaseCommentsTabComponent implements OnChanges {
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  /** Id of the release these comments belong to. */
  @Input() releaseId: string = '';

  /** Author oid of the release — used together with `roles` to decide if the current user can comment. */
  @Input() authorOid: string | undefined;

  /** Roles assigned on the release — used to decide if the current user can comment. */
  @Input() roles: ReleaseRoleViewModel[] = [];

  /** Current release status — commenting is blocked outside DRAFT/REVIEW/APPROVAL. */
  @Input() releaseStatus: ReleaseStatus | undefined;

  /** Emitted whenever the comment count/status summary changes, so the parent can update the tab label. */
  @Output() summaryChanged = new EventEmitter<ReleaseCommentsTabSummary>();

  readonly CommentResolutionStatus: typeof CommentResolutionStatus = CommentResolutionStatus;

  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  comments: ReleaseCommentViewModel[] = [];

  newCommentForm: FormGroup = this.formBuilder.group({
    content: ['', [Validators.required, Validators.minLength(3)]],
  });

  showNewCommentForm: boolean = false;
  activeReplyId: string | null = null;
  activeEditId: string | null = null;
  replyForms: { [commentId: string]: FormGroup } = {};
  editForms: { [commentId: string]: FormGroup } = {};

  /** Tracks in-flight resolution-status updates to prevent double-clicks. */
  updatingResolutionStatusIds: Set<string> = new Set<string>();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly releaseCommentsService: ReleaseCommentsService,
    private readonly userService: UserService,
    private readonly snackBar: SnackBarService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['releaseId'] && this.releaseId) {
      this.loadComments();
    }
  }

  /** Total comments including replies — used for the tab label badge. */
  get totalCommentCount(): number {
    let count: number = this.comments.length;
    for (const comment of this.comments) {
      count += (comment.replies ?? []).length;
    }
    return count;
  }

  /** True if at least one root comment is still Open — drives the tab's status text. */
  get hasOpenComments(): boolean {
    return this.comments.some((comment: ReleaseCommentViewModel) => comment.resolutionStatus === CommentResolutionStatus._0);
  }

  /** Human-readable summary shown in the tab label, e.g. "Comments (3 - Open)". */
  get tabStatusLabel(): string {
    return this.hasOpenComments ? 'Open' : 'Resolved';
  }

  /**
   * True while the release is in a state that allows commenting (DRAFT, REVIEW, APPROVAL)
   * and the current user is the release author or is assigned one of its roles.
   */
  get canComment(): boolean {
    if (!this.isReleaseCommentable) {
      return false;
    }
    return this.isCurrentUserParticipant;
  }

  /** Any participant of the release (not just the author) may change resolution status, same as pipe class comments. */
  get canChangeResolutionStatus(): boolean {
    return this.canComment;
  }

  private get isReleaseCommentable(): boolean {
    return this.releaseStatus === ReleaseStatus.DRAFT
      || this.releaseStatus === ReleaseStatus.REVIEW
      || this.releaseStatus === ReleaseStatus.APPROVAL;
  }

  private get isCurrentUserParticipant(): boolean {
    const currentOid: string | undefined = this.userService.getCurrentUser()?.idTokenClaims?.['oid'] as string | undefined;
    if (!currentOid) {
      return false;
    }
    if (this.authorOid && this.authorOid === currentOid) {
      return true;
    }
    return (this.roles ?? []).some((role: ReleaseRoleViewModel) => role.userId === currentOid);
  }

  loadComments(): void {
    this.releaseCommentsService.getComments(this.releaseId)
      .pipe(indicate(this.loading$), first(), takeUntilDestroyed(this.destroyRef))
      .subscribe((comments: ReleaseCommentViewModel[]) => {
        this.comments = comments ?? [];
        this.emitSummary();
      });
  }

  private emitSummary(): void {
    this.summaryChanged.emit({
      totalCount: this.totalCommentCount,
      hasOpenComments: this.hasOpenComments,
    });
  }

  startNewComment(): void {
    if (!this.canComment) {
      return;
    }
    this.showNewCommentForm = true;
    this.cancelActiveActions();
  }

  cancelNewComment(): void {
    this.showNewCommentForm = false;
    this.newCommentForm.reset();
  }

  submitNewComment(): void {
    if (!this.canComment || this.newCommentForm.invalid) {
      return;
    }

    const content: string = this.newCommentForm.value.content;
    const authorName: string = this.currentUserDisplayName();

    this.releaseCommentsService.createComment(this.releaseId, content, authorName)
      .pipe(
        indicate(this.saving$),
        first(),
        catchError((error: unknown) => {
          this.snackBar.showError(getApiErrorMessage(error, 'Failed to post comment. Please try again.'));
          return EMPTY;
        }),
      )
      .subscribe((createdComment: ReleaseCommentViewModel) => {
        createdComment.replies = createdComment.replies ?? [];
        this.comments.unshift(createdComment);
        this.showNewCommentForm = false;
        this.newCommentForm.reset();
        this.emitSummary();
      });
  }

  startReply(commentId: string): void {
    if (!this.canComment) {
      return;
    }
    this.cancelActiveActions();
    this.activeReplyId = commentId;

    if (!this.replyForms[commentId]) {
      this.replyForms[commentId] = this.formBuilder.group({
        content: ['', [Validators.required, Validators.minLength(3)]],
      });
    } else {
      this.replyForms[commentId].reset();
    }
  }

  cancelReply(commentId: string): void {
    this.replyForms[commentId]?.reset();
    this.activeReplyId = null;
  }

  submitReply(parentCommentId: string): void {
    const replyForm: FormGroup | undefined = this.replyForms[parentCommentId];
    const parentComment: ReleaseCommentViewModel | undefined = this.comments.find((comment: ReleaseCommentViewModel) => comment.id === parentCommentId);

    if (!this.canComment || !replyForm || replyForm.invalid || !parentComment) {
      return;
    }

    const content: string = replyForm.value.content;
    const authorName: string = this.currentUserDisplayName();

    this.releaseCommentsService.addReply(parentCommentId, content, authorName)
      .pipe(
        indicate(this.saving$),
        first(),
        catchError((error: unknown) => {
          this.snackBar.showError(getApiErrorMessage(error, 'Failed to post reply. Please try again.'));
          return EMPTY;
        }),
      )
      .subscribe((createdReply: ReleaseCommentViewModel) => {
        parentComment.replies = [...(parentComment.replies ?? []), createdReply];
        this.activeReplyId = null;
        replyForm.reset();
        this.emitSummary();
      });
  }

  startEdit(comment: ReleaseCommentViewModel): void {
    if (!this.canEditComment(comment) || !comment.id) {
      return;
    }
    this.cancelActiveActions();
    this.activeEditId = comment.id;

    if (!this.editForms[comment.id]) {
      this.editForms[comment.id] = this.formBuilder.group({
        content: [comment.content ?? '', [Validators.required, Validators.minLength(3)]],
      });
    } else {
      this.editForms[comment.id].patchValue({ content: comment.content ?? '' });
    }
  }

  cancelEdit(commentId: string): void {
    this.editForms[commentId]?.reset();
    this.activeEditId = null;
  }

  submitEdit(comment: ReleaseCommentViewModel): void {
    const editForm: FormGroup | undefined = comment.id ? this.editForms[comment.id] : undefined;
    if (!this.canEditComment(comment) || !editForm || editForm.invalid || !comment.id) {
      return;
    }

    const content: string = editForm.value.content;
    const resolutionStatus: CommentResolutionStatus = comment.resolutionStatus ?? CommentResolutionStatus._0;

    this.releaseCommentsService.updateComment(comment.id, content, resolutionStatus)
      .pipe(
        indicate(this.saving$),
        first(),
        catchError((error: unknown) => {
          this.snackBar.showError(getApiErrorMessage(error, 'Failed to update comment. Please try again.'));
          return EMPTY;
        }),
      )
      .subscribe((updatedComment: ReleaseCommentViewModel) => {
        comment.content = updatedComment.content;
        comment.updatedDate = updatedComment.updatedDate;
        this.activeEditId = null;
      });
  }

  canEditComment(comment: ReleaseCommentViewModel): boolean {
    return this.canComment && comment.authorId === this.currentUserOid();
  }

  canDeleteComment(comment: ReleaseCommentViewModel): boolean {
    return this.canEditComment(comment) && (comment.replies ?? []).length === 0;
  }

  deleteComment(comment: ReleaseCommentViewModel, parent?: ReleaseCommentViewModel): void {
    if (!this.canDeleteComment(comment) || !comment.id) {
      return;
    }

    this.releaseCommentsService.deleteComment(comment.id)
      .pipe(
        indicate(this.saving$),
        first(),
        catchError((error: unknown) => {
          this.snackBar.showError(getApiErrorMessage(error, 'Failed to delete comment. Please try again.'));
          return EMPTY;
        }),
      )
      .subscribe(() => {
        if (parent) {
          parent.replies = (parent.replies ?? []).filter((reply: ReleaseCommentViewModel) => reply.id !== comment.id);
        } else {
          this.comments = this.comments.filter((root: ReleaseCommentViewModel) => root.id !== comment.id);
        }
        this.emitSummary();
      });
  }

  updateResolutionStatus(comment: ReleaseCommentViewModel, newStatus: CommentResolutionStatus): void {
    if (!this.canChangeResolutionStatus || !comment.id || this.updatingResolutionStatusIds.has(comment.id)) {
      return;
    }

    const previousStatus: CommentResolutionStatus | undefined = comment.resolutionStatus;
    comment.resolutionStatus = newStatus;
    this.updatingResolutionStatusIds.add(comment.id);

    this.releaseCommentsService.updateResolutionStatus(comment.id, newStatus)
      .pipe(
        first(),
        tap({
          error: () => {
            comment.resolutionStatus = previousStatus;
          },
        }),
        catchError((error: unknown) => {
          this.snackBar.showError(getApiErrorMessage(error, 'Failed to update resolution status. Please try again.'));
          return EMPTY;
        }),
      )
      .subscribe({
        next: (updatedComment: ReleaseCommentViewModel) => {
          comment.resolutionStatus = updatedComment.resolutionStatus;
          this.emitSummary();
        },
        complete: () => {
          if (comment.id) {
            this.updatingResolutionStatusIds.delete(comment.id);
          }
        },
      });
  }

  getResolutionStatusLabel(status: CommentResolutionStatus | undefined): string {
    switch (status) {
      case CommentResolutionStatus._1: return 'Adopted';
      case CommentResolutionStatus._2: return 'Closed';
      default: return 'Open';
    }
  }

  /** Returns the display label for the author's role within the release. */
  getAuthorRoleLabel(role: UserType | undefined): string {
    switch (role) {
      case UserType._0: return 'Author';
      case UserType._1: return 'Owner';
      case UserType._2: return 'Internal Reviewer';
      case UserType._3: return 'External Reviewer';
      default: return 'Participant';
    }
  }

  getRelativeTime(date: Date | undefined): string {
    if (!date) {
      return '';
    }
    const now: Date = new Date();
    const diffMs: number = now.getTime() - new Date(date).getTime();
    const diffMins: number = Math.floor(diffMs / 60000);
    const diffHours: number = Math.floor(diffMins / 60);
    const diffDays: number = Math.floor(diffHours / 24);

    if (diffMins < 1) { return 'Just now'; }
    if (diffMins < 60) { return `${diffMins}m ago`; }
    if (diffHours < 24) { return `${diffHours}h ago`; }
    if (diffDays < 7) { return `${diffDays}d ago`; }
    return new Date(date).toLocaleDateString();
  }

  trackByCommentId(index: number, comment: ReleaseCommentViewModel): string {
    return comment.id ?? `${index}`;
  }

  private cancelActiveActions(): void {
    this.activeReplyId = null;
    this.activeEditId = null;
  }

  private currentUserOid(): string | undefined {
    return this.userService.getCurrentUser()?.idTokenClaims?.['oid'] as string | undefined;
  }

  private currentUserDisplayName(): string {
    const account = this.userService.getCurrentUser();
    return (account?.name || account?.username || 'Unknown User') as string;
  }
}
